// gkc_hash_code : 01GPFQ2BY4JCG0W281FKCRX39R
import { Dispatch, SetStateAction, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { selectUser } from 'redux/slices/userSlice';
import { DEFAULT_PAGE, PAGE_SIZE, DATE_FORMAT } from 'util/ConstantValues';
import { GetEmissionParams, Site, EdiStatus } from 'util/Types';
import {
  WorkHistoriesEmissionsData,
  WorkHistoriesParams,
} from 'types/workHistoriesTypes';
import styles from './HistoryCollects.module.scss';
import { RoleType, OperatorType } from 'util/Enums';
import { Column } from 'react-table';
import { getFormatDateWithCurrentTimezone } from 'ts/formatDate';
import { normalizeNumber, uniqRandomNumber } from 'util/commons';
import EdiStatusTag from 'components/atoms/EdiStatusTag';
import ResponsiveHistoryTable from 'components/molecules/ResponsiveHistoryTable';
import { getEmissionWorkHistories } from 'apis/work_histories/emission';
import CustomPagination from 'components/atoms/CustomPagination';
import _omit from 'lodash/omit';

export type Props = {
  id?: string;
  sites: Site[];
  isActive: boolean;
  filter?: GetEmissionParams;
  setCsvData?: Dispatch<SetStateAction<WorkHistoriesEmissionsData[]>>;
  wrapperStyle?: React.CSSProperties;
};

export const HistoryCollects = ({
  sites,
  isActive = false,
  filter,
  setCsvData,
  wrapperStyle,
}: Props) => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const [data, setData] = useState<WorkHistoriesEmissionsData[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [args, setArgs] = useState<
    WorkHistoriesParams & {
      trigger?: number;
    }
  >({
    page: DEFAULT_PAGE,
    take: PAGE_SIZE,
  });

  const pageCount = useMemo(
    () => Math.ceil(totalRecords / PAGE_SIZE),
    [totalRecords]
  );

  useEffect(() => {
    if ((user.role !== RoleType.AdminOperator && !args.siteId) || !isActive) {
      return;
    }

    getEmissionWorkHistories({
      type: 'collected',
      params: _omit(args, ['trigger']),
      successCallback: (data) => {
        setTotalRecords(data.total);
        setData(data.emissionsCollected);
      },
      failedCallback: () => {
        if (args.page === DEFAULT_PAGE) {
          setData([]);
          setTotalRecords(0);
        }
      },
    });
  }, [args, isActive, user.role]);

  useEffect(() => {
    setArgs({
      ...args,
      ...filter,
      ...(user.role !== RoleType.AdminOperator && {
        siteId: sites[0]?.childSites[0]?.id,
      }),
      page: DEFAULT_PAGE,
      trigger: uniqRandomNumber(),
    });
  }, [sites, filter, user.role]);

  useEffect(() => {
    setCsvData?.(data);
  }, [data]);

  const columns: Column<WorkHistoriesEmissionsData>[] = useMemo(
    () => [
      {
        Header: t('history_emission_card.card_registration_id') as string,
        accessor: 'id',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('history_emission_card.card_discharge_date_time') as string,
        accessor: (row) => row.emission.datetime,
        Cell: ({ value }) => (
          <>{getFormatDateWithCurrentTimezone(value, DATE_FORMAT.slaYMDHm)}</>
        ),
      },
      {
        Header: t('common.content') as string,
        accessor: (row) => row.emission.item,
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: t('common.weight') as string,
        accessor: (row) => row.emission.weight,
        Cell: ({ value }) => <>{normalizeNumber({ value })}kg</>,
      },
      {
        Header: t('history_emission_card.card_site') as string,
        accessor: (row) => row.emission.site,
        Cell: ({ value }) => <>{value}</>,
      },
      ...(user.role !== RoleType.AdminTenant
        ? [
            {
              Header: t('history_emission_card.card_ejector') as string,
              accessor: (row: WorkHistoriesEmissionsData) =>
                row.emission.tenant,
              Cell: ({ value }: { value: string }) => <>{value}</>,
            },
          ]
        : []),
      {
        Header: t('histories.label_csv_collection_date_time') as string,
        accessor: (row) => row.collect?.datetime || '',
        Cell: ({ row }) => (
          <>
            {row.original.collect
              ? getFormatDateWithCurrentTimezone(
                  row.original.collect.datetime,
                  DATE_FORMAT.slaYMDHm
                )
              : '-'}
          </>
        ),
      },
      {
        Header: t('histories.label_csv_delivery_company') as string,
        accessor: (row) => row.collect?.operatorName || '',
        Cell: ({ row }) => (
          <>{row.original.collect ? row.original.collect.operatorName : '-'}</>
        ),
      },
      {
        Header: t('histories.label_csv_recover') as string,
        accessor: (row) => row.collect?.userId || '',
        Cell: ({ row }) => (
          <>
            {row.original.collect
              ? `${row.original.collect.userId}(${row.original.collect.userName})`
              : '-'}
          </>
        ),
      },
      {
        Header: t('histories.label_manifest_number') as string,
        accessor: (row) => row.collect?.ediNumber || '',
        Cell: ({ row }) => (
          <>
            {row.original.collect &&
            row.original.collect.ediStatus != null &&
            ![EdiStatus.None, EdiStatus.Failed, EdiStatus.Processing].includes(
              row.original.collect.ediStatus
            )
              ? row.original.collect.ediNumber || '-'
              : '-'}
          </>
        ),
      },
      {
        Header: t('histories.label_edi_id') as string,
        accessor: (row) => row.collect?.ediStatus || EdiStatus.None,
        Cell: ({ row }) => (
          <>
            {row.original.collect &&
            row.original.collect.ediStatus != null &&
            row.original.collect.ediStatus !== EdiStatus.None ? (
              <EdiStatusTag
                status={row.original.collect.ediStatus}
                operatorType={OperatorType.Emissions}
              />
            ) : (
              '-'
            )}
          </>
        ),
      },
    ],
    [t, user.role, data.length]
  );

  return (
    <div className={styles.historyEmissions} style={wrapperStyle}>
      <div className={styles.tableContainer}>
        <ResponsiveHistoryTable<WorkHistoriesEmissionsData>
          data={data}
          columns={columns}
          scrollResetDependencies={[filter, data]}
          className={styles.historyTable}
        />
      </div>

      <div className={styles.paginationContainer}>
        <CustomPagination
          pageCount={pageCount}
          onPageChange={({ selected }) =>
            setArgs((prev) => ({ ...prev, page: selected + 1 }))
          }
          forcePage={args.page - 1}
        />
      </div>
    </div>
  );
};
